import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

const ITEMS = [
    {
        src: "/images/dark-gallery-banner.png",
        alt: "Savv Media banner — unearthing insights, creating demand, scaling impact",
        span: "col-span-2 md:col-span-4",
        ratio: "aspect-[5/2]",
        caption: "unearthing insights ♡",
    },
    {
        src: "/images/dark-gallery-face.png",
        alt: "Savv Media editorial artwork — Savv, the only way to be",
        span: "col-span-2 md:col-span-2",
        ratio: "aspect-square md:aspect-auto md:h-full",
        caption: "the only way to be",
    },
    {
        src: "/images/dark-gallery-hands.png",
        alt: "Savv and T-UGC Media hands artwork, established 2025",
        span: "col-span-1 md:col-span-2",
        ratio: "aspect-[4/5]",
        caption: "est. 2025",
    },
    {
        src: "/images/dark-gallery-mono.png",
        alt: "Savv Media SV monogram logo",
        span: "col-span-1 md:col-span-2",
        ratio: "aspect-[4/5]",
        caption: "the monogram",
    },
    {
        src: "/images/dark-gallery-moth.png",
        alt: "Savv Media moth logo",
        span: "col-span-2 md:col-span-2",
        ratio: "aspect-[2/1] md:aspect-[4/5]",
        caption: "the moth ♡",
    },
];

const Tile = ({ item, i, onOpen }) => (
    <Reveal delay={i * 0.08} className={item.span}>
        <motion.figure
            data-testid={`gallery-item-${i}`}
            whileHover={{ y: -6 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            onClick={() => onOpen(i)}
            className={`group relative h-full ${item.ratio} rounded-3xl overflow-hidden border-4 border-[#F1F1EF] shadow-[0_20px_50px_-20px_rgba(12,12,12,0.25)] bg-[#EAF1F7] cursor-zoom-in`}
        >
            <img
                src={item.src}
                alt={item.alt}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
            />
            <figcaption className="absolute bottom-3 left-3 bg-[#F1F1EF]/90 backdrop-blur-sm rounded-xl px-3.5 py-1.5 font-hand text-lg text-[#F1F1EF] rotate-[-1.5deg] opacity-0 translate-y-2 transition-all duration-400 group-hover:opacity-100 group-hover:translate-y-0">
                {item.caption}
            </figcaption>
        </motion.figure>
    </Reveal>
);

export const Gallery = () => {
    const [open, setOpen] = useState(null);

    const close = useCallback(() => setOpen(null), []);
    const step = useCallback(
        (dir) => setOpen((o) => (o + dir + ITEMS.length) % ITEMS.length),
        []
    );

    useEffect(() => {
        if (open === null) return;
        const onKey = (e) => {
            if (e.key === "Escape") close();
            if (e.key === "ArrowRight") step(1);
            if (e.key === "ArrowLeft") step(-1);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, close, step]);

    return (
        <section
            id="gallery"
            data-testid="gallery-section"
            className="py-20 md:py-32 px-5 sm:px-8 lg:px-12"
        >
            <div className="max-w-7xl mx-auto">
                <div className="flex flex-wrap items-end justify-between gap-6 mb-14 md:mb-20">
                    <div className="max-w-xl">
                        <Reveal>
                            <Eyebrow>The Gallery</Eyebrow>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <h2 className="mt-4 font-serif-ed text-3xl sm:text-4xl lg:text-5xl leading-[1.12] tracking-tight text-[#F1F1EF]">
                                Brand art,{" "}
                                <span className="italic text-[#D3A0A7]">
                                    out loud.
                                </span>
                            </h2>
                        </Reveal>
                    </div>
                    <Reveal delay={0.2}>
                        <p className="max-w-xs text-sm md:text-base leading-relaxed text-[#A9A6A0]">
                            Marks, moods and moments from the Savv universe.
                            Tap any piece to view it up close.
                        </p>
                    </Reveal>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-6 gap-4 lg:gap-5">
                    {ITEMS.map((item, i) => (
                        <Tile key={item.src} item={item} i={i} onOpen={setOpen} />
                    ))}
                </div>
            </div>

            <AnimatePresence>
                {open !== null && (
                    <motion.div
                        data-testid="gallery-lightbox"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        onClick={close}
                        className="fixed inset-0 z-[80] bg-[#0C0C0C]/92 backdrop-blur-md flex items-center justify-center p-4 md:p-10"
                    >
                        <button
                            data-testid="lightbox-close-btn"
                            onClick={close}
                            aria-label="Close"
                            className="absolute top-5 right-5 w-11 h-11 rounded-full bg-[#F1F1EF]/10 border border-[#F1F1EF]/25 text-[#F1F1EF] flex items-center justify-center hover:bg-[#C9DDEE] hover:text-[#F1F1EF] hover:border-[#C9DDEE] transition-colors duration-300"
                        >
                            <X size={20} />
                        </button>
                        <button
                            data-testid="lightbox-prev-btn"
                            onClick={(e) => {
                                e.stopPropagation();
                                step(-1);
                            }}
                            aria-label="Previous artwork"
                            className="absolute left-3 md:left-8 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-[#F1F1EF]/10 border border-[#F1F1EF]/25 text-[#F1F1EF] flex items-center justify-center hover:bg-[#C9DDEE] hover:text-[#F1F1EF] hover:border-[#C9DDEE] transition-colors duration-300"
                        >
                            <ChevronLeft size={20} />
                        </button>
                        <button
                            data-testid="lightbox-next-btn"
                            onClick={(e) => {
                                e.stopPropagation();
                                step(1);
                            }}
                            aria-label="Next artwork"
                            className="absolute right-3 md:right-8 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-[#F1F1EF]/10 border border-[#F1F1EF]/25 text-[#F1F1EF] flex items-center justify-center hover:bg-[#C9DDEE] hover:text-[#F1F1EF] hover:border-[#C9DDEE] transition-colors duration-300"
                        >
                            <ChevronRight size={20} />
                        </button>
                        <motion.figure
                            key={open}
                            initial={{ opacity: 0, scale: 0.94 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                            onClick={(e) => e.stopPropagation()}
                            className="max-w-5xl w-full"
                        >
                            <img
                                src={ITEMS[open].src}
                                alt={ITEMS[open].alt}
                                data-testid="lightbox-image"
                                className="w-full max-h-[78vh] object-contain rounded-2xl"
                            />
                            <figcaption className="mt-4 text-center font-hand text-2xl text-[#C9DDEE]">
                                {ITEMS[open].caption}
                            </figcaption>
                        </motion.figure>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
};
